import { useEffect, useState } from 'react';
import { FilePlus2, Plus, Trash2, X } from '../../lib/icons';
import { toast } from 'sonner';
import { fetchTiers, creerFactureLibre, UNITES_LIGNE } from '../../lib/dataProvider';
import { fmt } from '../shared/badges';
import { ContactCombobox } from '../shared/ContactCombobox';

const field =
  'mt-1.5 h-11 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm text-slate-900 outline-none transition-colors duration-150 focus:border-slate-900 focus:bg-white';
const label = 'text-xs font-bold uppercase tracking-[0.1em] text-slate-500';
const ligneVide = { designation: '', quantite: '', unite: 'forfait', prixUnitaire: '' };

export const FactureLibreDialog = ({ onClose, onCreated }) => {
  const aujourdhui = new Date().toISOString().slice(0, 10);
  const [tiers, setTiers] = useState([]);
  const [f, setF] = useState({ date: aujourdhui, tiersId: '', notes: '', type: 'diverse' });
  const [lignes, setLignes] = useState([{ ...ligneVide }]);

  useEffect(() => {
    fetchTiers().then(setTiers);
  }, []);

  const maj = (i, k, v) => setLignes((l) => l.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const total = lignes.reduce((s, l) => s + (Number(l.quantite) || 0) * (Number(l.prixUnitaire) || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.tiersId) return toast.error('Sélectionnez le client à facturer.');
    const { error, row } = await creerFactureLibre({ ...f, lignes });
    if (error) return toast.error(error);
    toast.success(`Facture interne ${row.id} — ${fmt(row.total, 3)} TND portés au compte de ${row.client}. Émettez le numéro officiel depuis le Facturier si nécessaire.`);
    onCreated?.(row);
  };

  return (
    <div data-testid="facture-libre-overlay" className="fixed inset-0 z-[65] flex items-center justify-center bg-slate-900/60 p-4">
      <form onSubmit={submit} data-testid="facture-libre-form" className="max-h-[92vh] w-full max-w-3xl overflow-y-auto border border-slate-200 bg-white">
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-lg font-black tracking-tight text-slate-900">
              <FilePlus2 size={18} /> Créer une facture diverse
            </h2>
            <p className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-500">
              Lignes libres hors stock — transport, prestations, services
            </p>
          </div>
          <button type="button" data-testid="facture-libre-close" onClick={onClose} className="border border-slate-200 p-2 text-slate-500 hover:bg-slate-900 hover:text-white">
            <X size={16} />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 px-5 py-5 sm:grid-cols-4">
          <div>
            <label className={label}>Nature</label>
            <select data-testid="fl-type" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className={field}>
              <option value="diverse">Services & prestations</option>
              <option value="transport">Transport & logistique</option>
            </select>
          </div>
          <div>
            <label className={label}>Date</label>
            <input data-testid="fl-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={field} />
          </div>
          <div>
            <label className={label}>Client à facturer</label>
            <ContactCombobox contacts={tiers} value={f.tiersId} onChange={(id) => setF({ ...f, tiersId: id })} testid="fl-client" />
          </div>
          <div>
            <label className={label}>Notes</label>
            <input data-testid="fl-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className={field} />
          </div>
        </div>

        <div className="px-5 pb-5">
          <p className={label}>Lignes libres</p>
          <div className="mt-2 space-y-2">
            {lignes.map((l, i) => (
              <div key={i} data-testid={`fl-ligne-${i}`} className="flex flex-wrap items-center gap-2 border border-slate-200 px-3 py-2">
                <input
                  data-testid={`fl-designation-${i}`}
                  placeholder="Désignation (ex. Frais de transport)"
                  value={l.designation}
                  onChange={(e) => maj(i, 'designation', e.target.value)}
                  className="h-11 min-w-[200px] flex-1 border border-slate-300 bg-slate-50 px-3 text-sm outline-none focus:border-slate-900 focus:bg-white"
                />
                <input
                  data-testid={`fl-quantite-${i}`}
                  type="number"
                  step="0.001"
                  min="0"
                  placeholder="Qté"
                  value={l.quantite}
                  onChange={(e) => maj(i, 'quantite', e.target.value)}
                  className="h-11 w-24 border border-slate-300 bg-slate-50 px-2 text-center font-data text-sm outline-none focus:border-slate-900 focus:bg-white"
                />
                <select
                  data-testid={`fl-unite-${i}`}
                  value={l.unite}
                  onChange={(e) => maj(i, 'unite', e.target.value)}
                  className="h-11 w-28 border border-slate-300 bg-slate-50 px-2 font-data text-sm outline-none focus:border-slate-900 focus:bg-white"
                >
                  {UNITES_LIGNE.map((u) => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </select>
                <input
                  data-testid={`fl-prix-${i}`}
                  type="number"
                  step="0.001"
                  min="0"
                  placeholder="Prix u."
                  value={l.prixUnitaire}
                  onChange={(e) => maj(i, 'prixUnitaire', e.target.value)}
                  className="h-11 w-28 border border-slate-300 bg-slate-50 px-2 text-center font-data text-sm outline-none focus:border-slate-900 focus:bg-white"
                />
                <span className="tabular w-28 text-right font-data text-sm font-semibold text-slate-900">
                  {fmt((Number(l.quantite) || 0) * (Number(l.prixUnitaire) || 0), 3)}
                </span>
                <button
                  type="button"
                  data-testid={`fl-retirer-${i}`}
                  onClick={() => setLignes((p) => (p.length === 1 ? p : p.filter((_, j) => j !== i)))}
                  className="border border-slate-300 p-2 text-slate-600 transition-colors duration-150 hover:bg-red-600 hover:text-white"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            data-testid="fl-ajouter-ligne"
            onClick={() => setLignes((p) => [...p, { ...ligneVide }])}
            className="mt-3 flex h-10 items-center gap-2 border border-slate-300 px-3 text-sm font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
          >
            <Plus size={15} /> Ajouter une ligne
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-4 border-t border-slate-200 px-5 py-4">
          <button type="submit" data-testid="fl-submit" className="h-11 flex-1 bg-slate-900 font-heading text-sm font-bold uppercase tracking-[0.08em] text-white transition-colors duration-150 hover:bg-slate-700">
            Valider la facture (créance client)
          </button>
          <p data-testid="fl-total" className="font-data text-sm font-semibold text-slate-900">Total : {fmt(total, 3)} TND</p>
        </div>
      </form>
    </div>
  );
};
